import { useCallback, useEffect, useRef, useState } from 'react';
import api from '@/lib/api';

const CHANGE_SETTLE_MS = 1500;
const STALE_AFTER_MS = 60 * 1000;

/*
  Sidebar badges (02 #8): GET /api/dashboard/nav-counts → { counts: { '/projects': { count, tone, label }, … } }.
  The server only counts modules the user can open. Loaded once per sign-in, again shortly after any change
  the user saves (api.js fires `app:mutation-success`), after an undo (`refreshKey`), and when the window
  regains focus after a minute. A failed load keeps the last numbers — the badges are only a convenience.
*/
export function useNavCounts(enabled, refreshKey) {
  const [counts, setCounts] = useState({});
  const loadedAt = useRef(0);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);

  const load = useCallback(async () => {
    loadedAt.current = Date.now();
    try {
      const res = await api.get('/dashboard/nav-counts');
      const next = res?.data?.counts;
      if (alive.current) setCounts(next && typeof next === 'object' ? next : {});
    } catch {
      // keep the last numbers
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      setCounts({});
      return;
    }
    load();
  }, [enabled, load, refreshKey]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return undefined;
    let timer = null;
    const onChange = () => {
      clearTimeout(timer);
      timer = setTimeout(load, CHANGE_SETTLE_MS);
    };
    const onFocus = () => {
      if (Date.now() - loadedAt.current > STALE_AFTER_MS) load();
    };
    window.addEventListener('app:mutation-success', onChange);
    window.addEventListener('focus', onFocus);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('app:mutation-success', onChange);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, load]);

  return counts;
}
